import { Injectable, Injector } from '@angular/core';
import {
  CheckboxQuestion,
  DropdownQuestion,
  QuestionBase,
  TextboxQuestion,
  QuestionGroup,
  EntityQuestion
} from 'core/components/forms';
import { TopicsService } from '../topics.service';
import { LocalesService } from 'core/services/locales.service';
import { Game } from '@models/game';
import { ClientsService } from 'core/services/clients.service';
import { TranslateService } from '@ngx-translate/core';
@Injectable()
export class GamesQuestionsService {
  public topics: TopicsService;

  constructor(
    public injector: Injector,
    public locales: LocalesService,
    public clients: ClientsService,
    protected translateService: TranslateService
  ) {
    this.topics = injector.get(TopicsService);
  }

  getQuestions(game: Game, client?: any) {
    const questions: QuestionBase<any>[] = [
      new QuestionGroup({
        isSection: true,
        title: 'games.create',
        class: 'col-1',
        childQuestions: this.getDataQuestions(game, client)
      }),
      new QuestionGroup({
        class: 'col-1',
        isSection: true,
        childQuestions: [
          new QuestionGroup({
            title: 'nouns.config',
            class: 'col-1',
            childQuestions: this.getConfigQuestions(game)
          })
        ]
      })
    ];
    return questions;
  }

  getDataQuestions(game: Game, client?: any) {
    const questions: QuestionBase<any>[] = [
      new EntityQuestion({
        key: 'clientId',
        label: 'nouns.client',
        class: 'col-2-offset',
        value: game ? game.clientId : null,
        entity: client,
        validators: { required: true },
        dataProvider: this.clients,
        editable: game ? false : true,
        disabled: game ? true : false,
        order: 1
      }),
      new TextboxQuestion({
        key: 'name',
        label: 'nouns.name',
        class: 'col-2',
        value: game ? game.name : null,
        validators: { required: true }
      }),
      new TextboxQuestion({
        key: 'canonicName',
        label: 'nouns.canonicName',
        class: 'col-2',
        value: game ? game.canonicName : null,
        validators: game ? {} : { required: true },
        editable: game ? false : true
      }),
      new TextboxQuestion({
        key: 'topic',
        label: 'games.topic',
        class: 'col-2-offset',
        value: game ? game.topic : null
      }),
      new TextboxQuestion({
        key: 'credits',
        label: 'games.credits',
        class: 'col-2-offset',
        value: game ? game.credits : null,
        validators: { required: true }
      }),
      new DropdownQuestion({
        key: 'languages',
        label: 'games.selectLanguages',
        class: 'col-2-offset',
        placeholder: 'actions.selectLanguages',
        value: game ? game.languages : null,
        optionsObservable: this.locales.getOptionsObservable(),
        multiple: true
      }),
      new TextboxQuestion({
        key: 'hours',
        label: 'nouns.hours',
        class: 'col-2-offset',
        value: game ? game.hours : null,
        validators: { required: false }
      }),
      new TextboxQuestion({
        key: 'minutes',
        label: 'nouns.minutes',
        class: 'col-2-offset',
        value: game ? game.minutes : null,
        validators: { required: false }
      })
    ];
    return questions.filter((question) => question != null);
  }

  getConfigQuestions(game: Game) {
    const questions: QuestionBase<any>[] = [
      new TextboxQuestion({
        key: 'backendHost',
        label: 'games.backendHost',
        class: 'col-1',
        value: game ? game.backendHost : null,
        validators: { required: true }
      }),
      new TextboxQuestion({
        key: 'loginUrl',
        label: 'games.loginUrl',
        class: 'col-1',
        value: game ? game.loginUrl : null,
        validators: { required: true }
      }),
      new DropdownQuestion({
        key: 'requireCustomization',
        label: 'games.properties.requireCustomization',
        class: 'col-2-offset',
        options: [
          { key: 'no', value: this.translateService.instant('games.properties.customization.no') },
          { key: 'optional', value: this.translateService.instant('games.properties.customization.optional') },
          { key: 'mandatory', value: this.translateService.instant('games.properties.customization.mandatory') }
        ],
        value: game ? game.requireCustomization : 'no'
      }),
      new QuestionGroup({
        key: 'configuration',
        class: 'col-1',
        childQuestions: [
          new TextboxQuestion({
            key: 'pointsFieldName',
            label: 'games.properties.pointsFieldName',
            class: 'col-2',
            value: game && game.configuration ? game.configuration.pointsFieldName : null
          }),
          new TextboxQuestion({
            key: 'turnFieldName',
            label: 'games.properties.turnFieldName',
            class: 'col-2',
            value: game && game.configuration ? game.configuration.turnFieldName : null
          }),
          new CheckboxQuestion({
            key: 'fixDatesFromGroup',
            label: 'games.properties.fixDatesFromGroup',
            class: 'col-1',
            value: game && game.configuration ? game.configuration.fixDatesFromGroup : false
          }),
          new CheckboxQuestion({
            key: 'shouldShowDocumentation',
            label: 'games.properties.shouldShowDocumentation',
            class: 'col-1',
            value: game && game.configuration ? game.configuration.shouldShowDocumentation : true
          }),
          new CheckboxQuestion({
            key: 'shouldSendDiploma',
            label: 'games.properties.shouldSendDiploma',
            class: 'col-1',
            value: game && game.configuration ? game.configuration.shouldSendDiploma : true
          }),
          new CheckboxQuestion({
            key: 'shouldShowBeginnersGuide',
            label: 'games.properties.shouldShowBeginnersGuide',
            class: 'col-1',
            value: game && game.configuration ? game.configuration.shouldShowBeginnersGuide : true
          })
        ]
      }),
      new CheckboxQuestion({
        key: 'disabled',
        label: 'games.properties.disabled',
        class: 'col-1',
        value: game ? game.disabled : false
      })
    ];
    return questions.filter((question) => question != null);
  }
}
